import type { RawGraphEdge2, RawGraphNode2 } from '../types';
import type { CollapsedGraphReference } from './types';
import { logError } from '@ardoq/logging';
import { isTruthy, WILDCARD } from '@ardoq/common-helpers';
import { componentInterface } from '@ardoq/component-interface';
import { uniq } from 'lodash';

const isVisible = ({
  id,
  componentMap,
  groupMap,
  collapsedGroupIds,
}: {
  id: string;
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  collapsedGroupIds: Set<string>;
}): boolean => {
  const item = componentMap.get(id) ?? groupMap.get(id);
  if (!item) {
    logError(Error('Invalid component or group.'), null, { id });
    return false;
  }
  if (!item.parentId) {
    return true;
  }
  if (collapsedGroupIds.has(WILDCARD) || collapsedGroupIds.has(item.parentId)) {
    return false;
  }
  return isVisible({
    id: item.parentId,
    componentMap,
    groupMap,
    collapsedGroupIds,
  });
};

const getUncollapsedAncestor = ({
  id,
  componentMap,
  groupMap,
  collapsedGroupIds,
}: {
  id: string;
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  collapsedGroupIds: Set<string>;
}): string => {
  if (isVisible({ id, componentMap, groupMap, collapsedGroupIds })) {
    return id;
  }
  const item = componentMap.get(id) ?? groupMap.get(id);
  if (!item) {
    logError(Error('Graph item not found.'), null, { id });
    return id;
  }
  if (!item.parentId) {
    logError(Error('Cannot find visible parent.'), null, { id });
    return id;
  }
  return getUncollapsedAncestor({
    id: item.parentId,
    componentMap,
    groupMap,
    collapsedGroupIds,
  });
};

const getComponentIdsOfCollapsedGroups = (
  collapsedGroupIds: string[],
  groupMap: Map<string, RawGraphNode2>
): string[] =>
  uniq(
    collapsedGroupIds.flatMap(collapsedGroupId =>
      getComponentIdsOfCollapsedGroup(collapsedGroupId, groupMap)
    )
  ).filter(componentInterface.isComponent);

const getComponentIdsOfCollapsedGroup = (
  collapsedGroupId: string,
  groupMap: Map<string, RawGraphNode2>
): string[] => {
  const { children, modelId: collapsedModelId } = groupMap.get(
    collapsedGroupId
  ) ?? { children: [] };
  return [
    ...(children?.flatMap(({ id, modelId }) =>
      groupMap.has(id)
        ? getComponentIdsOfCollapsedGroup(id, groupMap)
        : [modelId]
    ) ?? []),
    collapsedModelId,
  ].filter(isTruthy);
};

interface CollapseGraphArgs {
  componentMap: Map<string, RawGraphNode2>;
  referenceList: RawGraphEdge2[];
  groupMap: Map<string, RawGraphNode2>;
  collapsedGroupIds: Set<string>;
}
const collapseGraph = ({
  componentMap,
  referenceList,
  groupMap,
  collapsedGroupIds,
}: CollapseGraphArgs) => {
  const newReferenceList: CollapsedGraphReference[] = referenceList
    .map(reference => {
      const { sourceId, targetId } = reference;
      const newSourceId = getUncollapsedAncestor({
        id: sourceId,
        componentMap,
        groupMap,
        collapsedGroupIds,
      });
      const newTargetId = getUncollapsedAncestor({
        id: targetId,
        componentMap,
        groupMap,
        collapsedGroupIds,
      });
      const changed = newSourceId !== sourceId || newTargetId !== targetId;
      const isSelfReference = newSourceId === newTargetId;
      const isSelfReferenceFromCollapsedGroup = changed && isSelfReference;
      if (isSelfReferenceFromCollapsedGroup) {
        // nodes from collapsed groups should not create self referencing groups.
        return null;
      }
      return {
        ...reference,
        uncollapsedSourceId: sourceId,
        uncollapsedTargetId: targetId,
        sourceId: newSourceId,
        targetId: newTargetId,
      };
    })
    .filter(isTruthy);
  const newComponentMap = new Map(
    Array.from(componentMap).filter(([id]) =>
      isVisible({ id, componentMap, groupMap, collapsedGroupIds })
    )
  );
  const newGroupMap = new Map(
    Array.from(groupMap).filter(([id]) =>
      isVisible({ id, componentMap, groupMap, collapsedGroupIds })
    )
  );
  const componentIdsOfCollapsedGroups = getComponentIdsOfCollapsedGroups(
    Array.from(
      collapsedGroupIds.has(WILDCARD) ? newGroupMap.keys() : collapsedGroupIds
    ),
    groupMap
  );
  return {
    componentMap: newComponentMap,
    referenceList: newReferenceList,
    groupMap: newGroupMap,
    componentIdsOfCollapsedGroups,
  };
};

export { collapseGraph };
